import type { IncomeTaxBracket as RawTaxBracket } from '@/pages/blueprint-lite/types';

import type { TaxResidency } from '../types';

/**
 * Normalised ATO marginal tax bracket — numeric, decimal rate. Built from
 * the blueprint-lite `useFetchIncomeTaxBracketsQuery` response, which
 * stores values as strings and rate-on-excess as a percent integer
 * (e.g. `"30"` for 30%). We coerce to numbers + decimal here so the
 * pipeline never has to deal with stringly-typed money or percentage
 * conventions.
 */
export interface AtoTaxBracket {
  /** API value is "resident" or "non_resident". */
  taxResidency: 'resident' | 'non_resident';
  lowerIncomeThreshold: number;
  upperIncomeThreshold: number;
  /** Decimal — e.g. 0.30 for 30%. */
  taxRateOnExcess: number;
}

export function normaliseTaxBrackets(rows: RawTaxBracket[]): AtoTaxBracket[] {
  return rows.map((r) => ({
    taxResidency: r.taxResidency as AtoTaxBracket['taxResidency'],
    lowerIncomeThreshold: Number(r.lowerIncomeThreshold),
    upperIncomeThreshold: Number(r.upperIncomeThreshold),
    taxRateOnExcess: Number(r.taxRateOnExcess) / 100,
  }));
}

// Engine has 5 residency states; the ATO schedule only distinguishes
// resident vs non-resident. Part-year cases lean toward the residency they
// are transitioning to; "unsure" defaults to resident (most generous).
function residencySchedule(
  residency: TaxResidency,
): AtoTaxBracket['taxResidency'] {
  switch (residency) {
    case 'non_tax_resident':
    case 'part_year_tax_resident_leaving_aus':
      return 'non_resident';
    case 'australian_tax_resident':
    case 'part_year_tax_non_resident_returning_aus':
    case 'unsure':
    default:
      return 'resident';
  }
}

export function marginalRateFor(
  grossAud: number,
  residency: TaxResidency,
  brackets: AtoTaxBracket[],
): number {
  const schedule = residencySchedule(residency);
  const matching = brackets.filter((b) => b.taxResidency === schedule);
  if (matching.length === 0) {
    throw new Error(
      `ATO tax brackets unavailable for residency schedule "${schedule}"`,
    );
  }
  for (const b of matching) {
    if (grossAud >= b.lowerIncomeThreshold && grossAud <= b.upperIncomeThreshold) {
      return b.taxRateOnExcess;
    }
  }
  // Above the top bracket → return the top rate.
  const sorted = [...matching].sort(
    (a, b) => b.upperIncomeThreshold - a.upperIncomeThreshold,
  );
  return sorted[0].taxRateOnExcess;
}
